import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WatchButton } from "@/components/WatchButton";
import { Loader2, MapPin, Star, ArrowRight, Check } from "lucide-react";

export const REGIONS = {
  istanbul: {
    code: "IST", name: "Istanbul",
    hero: "https://images.unsplash.com/photo-1564407727371-3eece6c58961?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    tagline: "Where two continents meet",
    blurb: "From the Hagia Sophia and the Blue Mosque to the buzzing Grand Bazaar and Bosphorus sunsets, Istanbul blends imperial history with a restless, modern energy. Base yourself in Sultanahmet for the sights or Beyoğlu for nightlife.",
    highlights: ["Hagia Sophia & Blue Mosque", "Grand Bazaar shopping", "Bosphorus cruises", "Rooftop dining in Beyoğlu"],
  },
  antalya: {
    code: "AYT", name: "Antalya",
    hero: "https://images.pexels.com/photos/36625097/pexels-photo-36625097.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    tagline: "The turquoise coast",
    blurb: "Antalya pairs all-inclusive beach resorts with a beautifully preserved old town, Roman ruins and dramatic waterfalls. It's the gateway to the Turkish Riviera and the perfect family sun-and-sea escape.",
    highlights: ["Kaleiçi old town", "Düden waterfalls", "Konyaaltı & Lara beaches", "Ancient Perge & Aspendos"],
  },
  cappadocia: {
    code: "CAP", name: "Cappadocia",
    hero: "https://images.unsplash.com/photo-1631152282084-b8f1b380ccab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    tagline: "Fairy chimneys & sunrise balloons",
    blurb: "A surreal moonscape of fairy chimneys, cave hotels and underground cities. Wake before dawn for the world-famous hot-air balloon flights, then explore rock-cut churches and vineyards in Göreme and Ürgüp.",
    highlights: ["Sunrise balloon flights", "Göreme open-air museum", "Cave-hotel stays", "Underground cities"],
  },
  bodrum: {
    code: "BJV", name: "Bodrum",
    hero: "https://images.pexels.com/photos/34342668/pexels-photo-34342668.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    tagline: "Aegean glamour",
    blurb: "Whitewashed houses tumbling to a marina full of gulets, chic beach clubs and a legendary nightlife. Bodrum is the Aegean's most stylish resort town, ideal for boat trips and long lazy lunches by the sea.",
    highlights: ["Bodrum Castle & marina", "Gulet boat trips", "Beach clubs", "Bodrum nightlife"],
  },
  fethiye: {
    code: "FET", name: "Fethiye & Ölüdeniz",
    hero: "https://images.pexels.com/photos/16154341/pexels-photo-16154341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    tagline: "The Blue Lagoon",
    blurb: "Home to the iconic Blue Lagoon of Ölüdeniz — one of the most photographed beaches in the world — plus paragliding off Babadağ, the Lycian Way and turquoise cove cruises. Nature and adventure in equal measure.",
    highlights: ["Ölüdeniz Blue Lagoon", "Paragliding off Babadağ", "12 Islands boat trip", "The Lycian Way"],
  },
};

const Stars = ({ n }) => (
  <span className="flex items-center gap-0.5">{Array.from({ length: n || 0 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-[#C9A227] fill-[#C9A227]" />)}</span>
);

export default function RegionPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useApp();
  const { t } = useI18n();
  const region = REGIONS[slug];
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  const dflt = { ci: new Date(Date.now() + 21 * 864e5).toISOString().slice(0, 10), co: new Date(Date.now() + 24 * 864e5).toISOString().slice(0, 10) };

  useEffect(() => {
    if (!region) return;
    document.title = `${region.name} hotels — Vaya Holidays`;
    setLoading(true);
    api.post("/hotels/search", { destination: region.code, checkIn: dflt.ci, checkOut: dflt.co, adults: 2, rooms: 1, source: "auto" })
      .then((r) => setHotels((r.data.hotels || []).filter((h) => h.minRate != null).slice(0, 6)))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [slug]);

  if (!region) return (
    <div className="min-h-screen bg-[#F8FAFC]"><Header /><div className="pt-40 text-center text-[#475569]">Region not found. <Link to="/" className="text-[#009E9E] font-semibold">Home</Link></div></div>
  );

  const nights = 3;
  const openHotel = (h) => navigate(`/hotels/${h.code}?checkIn=${dflt.ci}&checkOut=${dflt.co}&adults=2&children=0&rooms=1&source=${h.src || "hbx"}`, { state: { hotel: h } });
  const allUrl = `/results/hotels?destination=${region.code}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`region-page-${slug}`}>
      <Header />

      <section className="relative pt-[68px]">
        <div className="absolute inset-0 h-[440px]">
          <img src={region.hero} alt={region.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2038] via-[#0B2038]/55 to-[#0B2038]/25" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 pt-24 pb-10">
          <div className="max-w-2xl fade-up">
            <span className="label-caps text-[#E3BE4F]">Türkiye</span>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mt-2">{region.name}</h1>
            <p className="text-[#C9A227] font-semibold text-lg mt-2">{region.tagline}</p>
            <p className="text-white/85 text-sm md:text-base mt-4">{region.blurb}</p>
            <Link to={allUrl} data-testid="region-search-cta" className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-bold transition-colors">
              {t("region_view_all")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {region.highlights.map((h, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex items-start gap-2.5">
              <Check className="w-5 h-5 text-[#009E9E] shrink-0" />
              <p className="text-sm font-semibold text-[#0B2038]">{h}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-14">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2038]">{t("region_top_hotels")} {region.name}</h2>
          <Link to={allUrl} className="text-sm font-bold text-[#009E9E] flex items-center gap-1">{t("region_view_all")} <ArrowRight className="w-4 h-4" /></Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#009E9E]" /></div>
        ) : hotels.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center text-[#475569]">
            {t("no_results")} <Link to={allUrl} className="text-[#009E9E] font-semibold">{t("region_view_all")}</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {hotels.map((h) => (
              <article key={`${h.src}-${h.code}`} data-testid={`region-hotel-${h.code}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden card-lift flex flex-col">
                <button onClick={() => openHotel(h)} className="h-48 bg-[#E2E8F0] relative">
                  {h.images?.[0] ? <img src={h.images[0]} alt={h.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#94A3B8]">Vaya</div>}
                </button>
                <div className="p-5 flex-1 flex flex-col">
                  <Stars n={h.stars} />
                  <h3 className="font-display text-lg font-extrabold text-[#0B2038] leading-tight mt-1.5 line-clamp-2 cursor-pointer hover:text-[#009E9E]" onClick={() => openHotel(h)}>{h.name}</h3>
                  <p className="text-[#475569] text-xs flex items-center gap-1 mt-1.5"><MapPin className="w-3.5 h-3.5 text-[#009E9E]" />{h.zoneName ? `${h.zoneName}, ` : ""}{h.destinationName}</p>
                  <div className="mt-auto flex items-end justify-between gap-2 pt-4 border-t border-[#E2E8F0] mt-4">
                    <div>
                      <p className="text-[11px] text-[#475569] font-semibold">{t("from")}</p>
                      <p className="font-display text-xl font-extrabold text-[#0B2038] leading-none">{formatPrice(h.minRate / nights)}</p>
                      <p className="text-[11px] text-[#475569] mt-1">{t("per_night")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <WatchButton compact payload={{ hotelCode: h.code, hotelName: h.name, image: h.images?.[0], destination: region.code, checkIn: dflt.ci, checkOut: dflt.co, adults: 2, children: 0, rooms: 1, source: h.src || "hbx", currentPriceEur: h.minRate ?? null }}
                        className="h-10 w-10 justify-center rounded-xl border border-[#E2E8F0] text-[#0B2038] hover:border-[#0B2038] hover:text-[#009E9E]" />
                      <button onClick={() => openHotel(h)} className="rounded-xl bg-[#009E9E] hover:bg-[#007A7A] text-white h-10 px-4 text-sm font-bold transition-colors">{t("book_now")}</button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
